// src/components/layout/PalliumCard.js
import React from 'react';

/**
 * PalliumCard - Styled card component for content containers
 * 
 * Provides consistent card styling with configurable padding and width options.
 * Can be used standalone or combined with PalliumPageBackground for centered layouts.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render inside the card
 * @param {boolean} props.centered - Whether to center the card in a flex container (default: false)
 * @param {'none' | 'default' | 'large'} props.padding - Padding size (default: 'default')
 * @param {'narrow' | 'default' | 'wide'} props.width - Max width preset (default: 'default')
 * @param {React.ReactNode} props.header - Optional header content
 * @param {React.ReactNode} props.footer - Optional footer content
 * @param {string} props.className - Additional classes for the card wrapper
 */
const PalliumCard = ({ 
    children, 
    centered = false, 
    padding = 'default', 
    width = 'default',
    header,
    footer,
    className = ''
}) => {
    // Determine max-width based on width prop
    const getMaxWidth = () => {
        switch (width) {
            case 'narrow':
                return '500px';
            case 'wide':
                return '770px';
            default:
                return '700px';
        }
    };

    // Determine padding classes based on padding prop
    const getPaddingClasses = () => {
        switch (padding) {
            case 'none':
                return '';
            case 'large':
                return 'p-6 md:p-8';
            default:
                return 'p-4 md:p-6';
        }
    };

    const cardContent = (
        <div 
            className={`bg-white rounded-xl border-none ${className}`}
            style={{ 
                maxWidth: centered ? getMaxWidth() : undefined,
                width: centered ? '100%' : undefined,
                boxShadow: 'var(--pallium-shadow-lg)'
            }}
        >
            {/* Header */}
            {header && (
                <div 
                    className="px-6 py-4"
                    style={{ 
                        borderBottom: '1px solid var(--pallium-stroke-lighter)' 
                    }}
                >
                    {header}
                </div>
            )}
            
            {/* Body */}
            <div className={getPaddingClasses()}>
                {children}
            </div>

            {/* Footer */}
            {footer && (
                <div 
                    className="px-6 py-4"
                    style={{ 
                        borderTop: '1px solid var(--pallium-stroke-lighter)' 
                    }}
                >
                    {footer}
                </div>
            )}
        </div>
    );

    // If centered, wrap in a flex container
    if (centered) {
        return (
            <div className="flex items-center justify-center p-4 md:p-6 w-full">
                {cardContent}
            </div>
        );
    }

    return cardContent;
};

export default PalliumCard;

